/**
 * /account/trade-ins — customer's trade-in estimates.
 * Each row: vehicle info, mileage, estimate range, "Submit to dealer" CTA if not yet submitted.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Car } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";

const fmtUSD = (cents) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format((cents || 0) / 100);
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";

const TradeIns = () => {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [submitEst, setSubmitEst] = useState(null);
  const [msg, setMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get("/me/trade-in-estimates").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const submitEstimate = async (e) => {
    e.preventDefault();
    if (!submitEst) return;
    setSubmitting(true);
    try {
      await api.post("/trade-in/estimate/submit", {
        estimate_id: submitEst.id,
        name: user.full_name, email: user.email, phone: user.phone || "",
        message: msg,
      });
      toast.success("Submitted — a BeloMotors specialist will follow up.");
      setSubmitEst(null);
      setMsg("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="trade-ins-page">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Your saved</p>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
            Trade-in estimates
          </h1>
        </div>
        <Button asChild variant="outline" className="border-brand text-brand hover:bg-brand hover:text-white">
          <Link to="/account">← Account</Link>
        </Button>
      </div>

      {items === null ? (
        <div className="mt-10 space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-32 bg-slate-100 rounded-2xl animate-pulse" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="mt-16 text-center bg-slate-50 border border-slate-200 rounded-2xl py-16" data-testid="trade-ins-empty">
          <Car size={30} className="mx-auto text-slate-300" />
          <p className="mt-4 font-display text-xl text-slate-800">No trade-in estimates yet</p>
          <p className="mt-2 text-sm text-slate-500">
            Open any vehicle and use the "Have a trade-in?" panel to get an instant estimate.
          </p>
          <Button asChild className="mt-6 bg-brand hover:bg-brand-hover text-white">
            <Link to="/inventory">Browse inventory</Link>
          </Button>
        </div>
      ) : (
        <div className="mt-8 space-y-4" data-testid="trade-ins-list">
          {items.map((e) => (
            <div key={e.id} className="rounded-2xl border-2 border-gold/50 bg-white p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid={`trade-in-row-${e.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="w-8 h-8 rounded-full bg-gold text-slate-900 flex items-center justify-center shrink-0">
                    <Car size={14} />
                  </span>
                  <div>
                    <p className="font-display text-lg font-semibold text-slate-900">
                      {e.year} {e.make} {e.model}
                      {e.trim && <span className="text-slate-500 font-normal text-sm"> ({e.trim})</span>}
                    </p>
                    <p className="text-xs text-slate-500">
                      {e.mileage?.toLocaleString()} mi · {e.condition}
                      {e.zip_code ? ` · ZIP ${e.zip_code}` : ""} · Saved {fmtDate(e.created_at)}
                    </p>
                  </div>
                </div>
                <div className="mt-3 rounded-lg bg-gold/10 border border-gold/30 px-3 py-2 inline-block">
                  <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mr-2">Est. range</span>
                  <span className="font-display text-lg font-bold text-slate-900">
                    {fmtUSD(e.estimate_low_cents)} – {fmtUSD(e.estimate_high_cents)}
                  </span>
                  <span className="ml-2 text-xs text-slate-600">(mid <strong>{fmtUSD(e.estimate_mid_cents)}</strong>)</span>
                </div>
              </div>
              <div className="flex flex-col gap-2 md:min-w-[200px]">
                {e.submitted_to_dealer ? (
                  <div className="text-center rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-800 font-semibold" data-testid={`trade-in-submitted-badge-${e.id}`}>
                    ✓ Submitted for appraisal
                  </div>
                ) : (
                  <Button
                    className="bg-gold hover:bg-gold/90 text-slate-900 font-semibold"
                    onClick={() => setSubmitEst(e)}
                    data-testid={`trade-in-submit-btn-${e.id}`}
                  >
                    Submit to dealer
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!submitEst} onOpenChange={(o) => !o && setSubmitEst(null)}>
        <DialogContent className="max-w-md" data-testid="trade-in-submit-dialog">
          <DialogHeader>
            <DialogTitle>Submit for official appraisal</DialogTitle>
            <DialogDescription>
              A BeloMotors specialist will reach out to schedule your in-person appraisal.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitEstimate} className="space-y-3">
            {submitEst && (
              <div className="text-xs bg-gold/10 border border-gold/30 rounded-lg px-3 py-2 text-slate-800">
                <strong>{submitEst.year} {submitEst.make} {submitEst.model}</strong> · Est. {fmtUSD(submitEst.estimate_low_cents)}–{fmtUSD(submitEst.estimate_high_cents)}
              </div>
            )}
            <div>
              <Label>Notes (optional)</Label>
              <Textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={2} placeholder="Best time to visit, particular concerns…" />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setSubmitEst(null)}>Cancel</Button>
              <Button type="submit" className="bg-gold hover:bg-gold/90 text-slate-900 font-semibold" disabled={submitting} data-testid="trade-in-submit-confirm-btn">
                {submitting ? "Submitting…" : "Confirm"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TradeIns;
